export type Theme = 'light' | 'dark';

export interface CupVisualMeta {
  className: string;
  label: string;
  detail: string;
}

export interface RecentHydrationEvent {
  type: string;
  occurredAt?: string | null;
  receivedAt?: string | null;
}

export type HydrationProgressBand =
  | 'empty'
  | 'starting'
  | 'building'
  | 'steady'
  | 'near'
  | 'goal'
  | 'overflow'
  | 'overflow-strong'
  | 'overflow-capped';

export interface HydrationProgressState {
  percent: number;
  visualPercent: number;
  band: HydrationProgressBand;
  summary: string;
}

export interface HydrationSnapshot {
  totalMl: number;
  drinkCount: number;
}

export interface DrinkTransition {
  amountMl: number;
  countDelta: number;
  crossedGoal: boolean;
}

export interface HistoryDay {
  local_date: string;
  total_ml: number;
  drink_count: number;
}

export interface HistorySummary {
  totalMl: number;
  activeDays: number;
  goalDays: number;
  averageActiveMl: number;
  longestGoalStreak: number;
  drinkCount: number;
  averageDrinkMl: number;
}

export function resolveTheme(stored: string | null, prefersDark: boolean): Theme {
  if (stored === 'light' || stored === 'dark') return stored;
  return prefersDark ? 'dark' : 'light';
}

export function normalisePairingCode(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

export function hydrationProgressState(totalValue: number, goalValue: number): HydrationProgressState {
  const total = Math.max(0, Math.round(Number(totalValue) || 0));
  const goal = Math.max(1, Math.round(Number(goalValue) || 1));
  const percent = Math.max(0, Math.round(total / goal * 100));
  let band: HydrationProgressBand;
  if (percent === 0) band = 'empty';
  else if (percent < 25) band = 'starting';
  else if (percent < 50) band = 'building';
  else if (percent < 75) band = 'steady';
  else if (percent < 100) band = 'near';
  else if (percent === 100) band = 'goal';
  else if (percent < 120) band = 'overflow';
  else if (percent < 150) band = 'overflow-strong';
  else band = 'overflow-capped';
  const summary = percent === 0
    ? '今天從第一口開始'
    : percent < 100
      ? `今天還差 ${Math.max(0, goal - total)} ml 達成目標`
      : percent === 100
        ? '今天剛好達成飲水目標'
        : `今天已超過目標 ${Math.max(0, total - goal)} ml`;
  return { percent, visualPercent: Math.min(100, percent), band, summary };
}

export function drinkTransition(previous: HydrationSnapshot | null,
                                current: HydrationSnapshot,
                                goalValue: number): DrinkTransition | null {
  if (!previous) return null;
  const goal = Math.max(1, Math.round(Number(goalValue) || 1));
  const resetObserved = current.drinkCount < previous.drinkCount || current.totalMl < previous.totalMl;
  if (resetObserved) {
    const amount = Math.max(0, Math.round(current.totalMl));
    if (current.drinkCount <= 0 || amount <= 0) return null;
    return {
      amountMl: amount,
      countDelta: current.drinkCount,
      crossedGoal: current.totalMl - amount < goal && current.totalMl >= goal,
    };
  }
  if (current.drinkCount <= previous.drinkCount || current.totalMl <= previous.totalMl) return null;
  return {
    amountMl: Math.max(1, Math.round(current.totalMl - previous.totalMl)),
    countDelta: current.drinkCount - previous.drinkCount,
    crossedGoal: previous.totalMl < goal && current.totalMl >= goal,
  };
}

export function recentDateKeys(endDateKey: string, countValue: number): string[] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endDateKey);
  if (!match) return [];
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const end = new Date(Date.UTC(year, month - 1, day));
  if (end.getUTCFullYear() !== year || end.getUTCMonth() !== month - 1 || end.getUTCDate() !== day) return [];
  const count = Math.min(366, Math.max(1, Math.floor(Number(countValue) || 1)));
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(end);
    date.setUTCDate(end.getUTCDate() - (count - index - 1));
    return date.toISOString().slice(0, 10);
  });
}

export function calendarYearDateKeys(yearValue: number): string[] {
  const year = Math.floor(Number(yearValue));
  if (!Number.isSafeInteger(year) || year < 1000 || year > 9999) return [];
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return recentDateKeys(`${year}-12-31`, leap ? 366 : 365);
}

export function shiftHeatmapYear(selectedYear: number | null, directionValue: number,
                                 currentYearValue: number): number | null {
  const currentYear = Math.floor(Number(currentYearValue));
  if (!Number.isSafeInteger(currentYear) || currentYear < 1000 || currentYear > 9999) return null;
  const direction = directionValue < 0 ? -1 : 1;
  if (selectedYear === null) return direction < 0 ? currentYear - 1 : currentYear;
  const selected = Math.min(currentYear, Math.max(1000, Math.floor(Number(selectedYear))));
  if (direction > 0 && selected >= currentYear) return null;
  return Math.max(1000, selected + direction);
}

export function historySummary(days: HistoryDay[], goalValue: number): HistorySummary {
  const goal = Math.max(1, Math.round(Number(goalValue) || 1));
  let totalMl = 0;
  let activeDays = 0;
  let goalDays = 0;
  let longestGoalStreak = 0;
  let currentGoalStreak = 0;
  let drinkCount = 0;
  for (const day of days) {
    const total = Math.max(0, Number(day.total_ml) || 0);
    const count = Math.max(0, Math.round(Number(day.drink_count) || 0));
    totalMl += total;
    drinkCount += count;
    if (total > 0 || count > 0) activeDays++;
    if (total >= goal) {
      goalDays++;
      longestGoalStreak = Math.max(longestGoalStreak, ++currentGoalStreak);
    } else {
      currentGoalStreak = 0;
    }
  }
  return {
    totalMl: Math.round(totalMl),
    activeDays,
    goalDays,
    averageActiveMl: activeDays ? Math.round(totalMl / activeDays) : 0,
    longestGoalStreak,
    drinkCount,
    averageDrinkMl: drinkCount ? Math.round(totalMl / drinkCount) : 0,
  };
}

export function historyHeatLevel(totalValue: number, goalValue: number, hasRecord: boolean): number {
  if (!hasRecord) return 0;
  const total = Math.max(0, Number(totalValue) || 0);
  const goal = Math.max(1, Number(goalValue) || 1);
  const ratio = total / goal;
  if (ratio >= 1) return 4;
  if (ratio >= .75) return 3;
  if (ratio >= .5) return 2;
  return 1;
}

export function normaliseCupState(value?: string): string {
  const states: Record<string, string> = {
    NO_CUP: 'no_cup',
    CUP_UNSTABLE: 'unstable',
    CUP_STABLE: 'stable',
    POSSIBLE_DRINK: 'possible_drink',
    DRINK_CONFIRMED: 'drink_confirmed',
    REFILL_DETECTED: 'refill_detected',
  };
  const key = String(value ?? '').trim().toUpperCase();
  return states[key] ?? String(value ?? '').trim().toLowerCase();
}

export function cupStateForDisplay(liveState: string | undefined, event: RecentHydrationEvent | null | undefined,
                                   now = Date.now()): string {
  const current = normaliseCupState(liveState);
  if (!event || !['drink', 'refill'].includes(event.type)) return current;
  const eventAt = Date.parse(event.receivedAt || event.occurredAt || '');
  const age = now - eventAt;
  if (!Number.isFinite(eventAt) || age < -60_000 || age > 120_000) return current;
  return event.type === 'drink' ? 'drink_confirmed' : 'refill_detected';
}

export function cupVisualMeta(cupState?: string, reminderState?: string): CupVisualMeta {
  const state = normaliseCupState(cupState);
  if (state === 'drink_confirmed') {
    return {
      className: 'is-fresh',
      label: '剛完成一次飲水',
      detail: '新的提醒週期已從這次飲水重新開始。',
    };
  }

  if (state === 'refill' || state === 'refill_detected') {
    return {
      className: 'is-refill',
      label: '偵測到補水',
      detail: '補水不會被算成飲水量。',
    };
  }

  if (reminderState === 'paused_today') {
    return {
      className: 'is-paused',
      label: '今日提醒已暫停',
      detail: '杯子仍會記錄飲水，提醒會在明天恢復。',
    };
  }

  if (reminderState === 'snoozed') {
    return {
      className: 'is-snoozed',
      label: '提醒稍後再響',
      detail: '杯子正在等待下一個提醒時間。',
    };
  }

  switch (state) {
    case 'stable':
    case 'counting':
      return {
        className: 'is-resting',
        label: '水杯已放妥',
        detail: '正在觀察重量，拿起喝水後會重新計時。',
      };
    case 'unstable':
      return {
        className: 'is-settling',
        label: '水杯正在穩定',
        detail: '請讓杯子靜置片刻，完成重量判讀。',
      };
    case 'possible_drink':
      return {
        className: 'is-lifted',
        label: '偵測到拿起水杯',
        detail: '放回杯子後會確認本次飲水量。',
      };
    case 'no_cup':
    default:
      return {
        className: 'is-away',
        label: '等待放上水杯',
        detail: '放上水杯並穩定後，才會開始飲水與提醒計時。',
      };
  }
}
